"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useEventsTableSchema = exports.query = exports.granularities = exports.metric = exports.dimension = exports.viewVersions = exports.persistedWidgetViewToQueryView = exports.SCORES_LISTABLE_COUNT_VIEW = exports.viewsV2 = exports.views = exports.viewDeclaration = exports.metricAggregations = void 0;
exports.getValidAggregationsForMeasureType = getValidAggregationsForMeasureType;
const zod_1 = require("zod");
const filters_1 = require("../../interfaces/filters");
exports.metricAggregations = zod_1.z.enum([
    "sum",
    "avg",
    "count",
    "max",
    "min",
    "p50",
    "p75",
    "p90",
    "p95",
    "p99",
    "histogram",
    "uniq",
]);
exports.viewDeclaration = zod_1.z.object({
    name: zod_1.z.string(),
    description: zod_1.z.string(),
    // This is the basic statement that we query from. Usually, this should be the view_name + FINAL or a more complex subquery.
    baseCte: zod_1.z.string(),
    dimensions: zod_1.z.record(zod_1.z.string(), zod_1.z.object({
        sql: zod_1.z.string(),
        alias: zod_1.z.string().optional(),
        relationTable: zod_1.z.string().optional(),
        description: zod_1.z.string().optional(),
        type: zod_1.z.string().optional(),
        unit: zod_1.z.string().optional(),
        aggregationFunction: zod_1.z.string().optional(),
        // Override for filter generation when the dimension uses complex SQL/aggregation.
        // where: column expressions OR'd together for pre-aggregation row pruning.
        // The exact match uses dimension.sql (the row-level expression).
        filterSql: zod_1.z
            .object({
            where: zod_1.z.array(zod_1.z.string()),
        })
            .optional(),
        highCardinality: zod_1.z.boolean().optional(),
        uiHidden: zod_1.z.boolean().optional(),
        // Expands dimension.sql independently with arrayJoin(), producing one row
        // per array element. Duplicate elements duplicate the source row; use
        // arrayDistinct(...) in sql when the intended grain is entity + distinct value.
        explodeArray: zod_1.z.boolean().optional(),
        // Expands dimension.sql and valuesSql together in one ARRAY JOIN, pairing
        // elements by position and exposing valueAlias to a dependent measure.
        // Only one pairExpand dimension is supported per query, and it cannot be filtered.
        pairExpand: zod_1.z
            .object({
            valuesSql: zod_1.z.string(),
            valueAlias: zod_1.z.string(),
        })
            .optional(),
    })),
    measures: zod_1.z.record(zod_1.z.string(), zod_1.z.object({
        sql: zod_1.z.string(),
        alias: zod_1.z.string().optional(),
        relationTable: zod_1.z.string().optional(),
        description: zod_1.z.string().optional(),
        type: zod_1.z.string().optional(),
        unit: zod_1.z.string().optional(),
        // Natural aggregation the widget builder preselects when the user
        // switches to this measure (e.g. `sum` for toolCalls, where a carried-over
        // `count` would count observations instead of tool calls). UI-only
        // default; the query builder never reads it.
        defaultAggregation: exports.metricAggregations.optional(),
        aggs: zod_1.z.record(zod_1.z.string(), zod_1.z.string()).optional(),
        // Override query semantics for specific user-selected aggregations while
        // keeping the base declaration as the UI/default compatibility contract.
        aggregationOverrides: zod_1.z
            .record(zod_1.z.string(), zod_1.z.object({
            sql: zod_1.z.string(),
            type: zod_1.z.string().optional(),
            aggs: zod_1.z.record(zod_1.z.string(), zod_1.z.string()).optional(),
            queryAggregation: exports.metricAggregations.optional(),
        }))
            .optional(),
        // Auto-includes a dimension needed to evaluate the measure, including its
        // explodeArray or pairExpand behavior and resulting aliases.
        requiresDimension: zod_1.z.string().optional(),
    })),
    tableRelations: zod_1.z.record(zod_1.z.string(), zod_1.z.object({
        name: zod_1.z.string(),
        joinConditionSql: zod_1.z.string(),
        timeDimension: zod_1.z.string(),
        useFinal: zod_1.z.boolean().optional(),
    })),
    // Segments are used to apply "constant" filters to the query. For example, if we only want one type of observations.
    segments: filters_1.singleFilterList,
    timeDimension: zod_1.z.string(),
    // When set, adds a subquery filter to restrict rows to those whose "root event"
    // (matching the condition) has timeDimension in the query window.
    rootEventCondition: zod_1.z
        .object({
        // The column used to match root entities between outer query and subquery (e.g., "trace_id").
        column: zod_1.z.string(),
        // Fully qualified, self-contained SQL condition identifying root events.
        condition: zod_1.z.string(),
    })
        .optional(),
});
const stringDateTime = zod_1.z.iso.datetime({ offset: true });
exports.views = zod_1.z.enum([
    "traces",
    "observations",
    "scores-numeric",
    "scores-categorical",
    "scores-boolean",
    // "sessions",
    // "users",
]);
// Public v2 API views - excludes "traces". Internal dashboard queries still
// support the events-backed v2 traces declaration for legacy widget parity.
exports.viewsV2 = zod_1.z.enum([
    "observations",
    "scores-numeric",
    "scores-categorical",
    "scores-boolean",
]);
/**
 * Internal-only view name (see `dataModel.ts`), deliberately NOT a member of
 * `views`/`viewsV2` — those are iterated by the public metrics API and the
 * widget-builder view picker, and this powers only one internal call site.
 * Unioned onto the internal `query` schema below instead.
 */
exports.SCORES_LISTABLE_COUNT_VIEW = "scores-listable-count";
/**
 * Persisted dashboard-widget view ids → query view ids. Lives here (not with
 * the server-only DashboardService types) so client code can classify a stored
 * widget; DashboardService's Prisma-keyed map is this constant, so the two
 * cannot drift.
 */
exports.persistedWidgetViewToQueryView = {
    TRACES: "traces",
    OBSERVATIONS: "observations",
    SCORES_NUMERIC: "scores-numeric",
    SCORES_BOOLEAN: "scores-boolean",
    SCORES_CATEGORICAL: "scores-categorical",
};
exports.viewVersions = zod_1.z.enum(["v1", "v2"]);
exports.dimension = zod_1.z.object({
    field: zod_1.z.string(),
});
/** getValidAggregationsForMeasureType returns the aggregations valid for a measure type: every aggregation for numeric types, or `count`/`uniq` otherwise. */
function getValidAggregationsForMeasureType(measureType) {
    if (measureType === "integer" ||
        measureType === "decimal" ||
        measureType === "number") {
        return [...exports.metricAggregations.options];
    }
    return ["count", "uniq"];
}
exports.metric = zod_1.z.object({
    measure: zod_1.z.string(),
    aggregation: exports.metricAggregations,
});
/** granularities is the superset of time-bucket tokens: the 6 base options plus the 10 Monitor window increments. */
exports.granularities = zod_1.z.enum([
    "auto",
    "minute",
    "hour",
    "day",
    "week",
    "month",
    "5m",
    "10m",
    "15m",
    "30m",
    "1h",
    "2h",
    "4h",
    "1d",
    "2d",
    "1w",
]);
exports.query = zod_1.z
    .object({
    // See `SCORES_LISTABLE_COUNT_VIEW`'s doc comment: unioned on here (the
    // internal query schema) rather than added to `views` itself, so it
    // stays out of the public metrics API and the widget-builder view
    // picker, both of which read `views`/`viewsV2` directly.
    view: zod_1.z.union([exports.views, zod_1.z.literal(exports.SCORES_LISTABLE_COUNT_VIEW)]),
    dimensions: zod_1.z.array(exports.dimension),
    metrics: zod_1.z.array(exports.metric),
    filters: filters_1.singleFilterList,
    timeDimension: zod_1.z
        .object({
        // TODO: We may want to extend this and allow custom intervals like 3h in the future.
        // auto tries to bin the data into approximately 50 buckets given the time range
        granularity: exports.granularities,
    })
        .nullable(),
    // Entity dimension for bucketing by a categorical field (e.g., experimentName).
    // IMPORTANT: Unlike timeDimension which has implicit bucket limits (24 hours/day),
    // entityDimension has NO cardinality guarantee. Callers MUST filter the same
    // entity field in WHERE before GROUP BY runs (max ~50 values).
    // Without this pre-filtering, GROUP BY on high-cardinality columns will be slow/OOM.
    entityDimension: zod_1.z
        .object({
        field: zod_1.z.string(), // e.g., "experimentName"
    })
        .nullish(),
    fromTimestamp: stringDateTime,
    toTimestamp: stringDateTime,
    orderBy: zod_1.z
        .array(zod_1.z.object({
        field: zod_1.z.string(),
        direction: zod_1.z.enum(["asc", "desc"]),
    }))
        .nullable(),
    // Chart configuration for chart-specific settings like histogram bins and pivot table dimensions
    chartConfig: zod_1.z
        .object({
        type: zod_1.z.string(),
        bins: zod_1.z.number().int().min(1).max(100).optional(),
        row_limit: zod_1.z.number().int().positive().lte(1000).optional(),
    })
        .optional(),
})
    .refine((query) => 
// Ensure fromTimestamp is before toTimestamp
new Date(query.fromTimestamp) < new Date(query.toTimestamp), { message: "fromTimestamp must be before toTimestamp" })
    .refine((query) => 
// timeDimension and entityDimension are mutually exclusive
!(query.timeDimension && query.entityDimension), { message: "timeDimension and entityDimension are mutually exclusive" });
exports.useEventsTableSchema = zod_1.z
    .union([zod_1.z.literal("true"), zod_1.z.literal("false"), zod_1.z.boolean()])
    .optional()
    .transform((val) => val === "true" || val === true);
